class MegaMenuController {
  constructor() {
    this.menuItems = document.querySelectorAll('.mega-menu-item');
    this.overlay = document.querySelector('.mega-menu-overlay');
    this.closeTimers = new Map();
    this.CLOSE_DELAY = 300;

    this.bindDesktopEvents();
    this.bindMobileEvents();
    this.bindKeyboard();
    this.bindSearchOverlay();
  }

  bindDesktopEvents() {
    this.menuItems.forEach(item => {
      const dropdown = item.querySelector('.mega-menu-dropdown');
      if (!dropdown) return;

      item.addEventListener('mouseenter', () => this.open(item));
      item.addEventListener('mouseleave', () => this.scheduleClose(item));

      const trigger = item.querySelector('.mega-menu-trigger');
      if (trigger) {
        trigger.addEventListener('focus', () => this.open(item));
      }
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeAll();
        this.closeSearch();
      });
    }

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.mega-menu-item') &&
          !e.target.closest('.mega-menu-overlay') &&
          !e.target.closest('.header__search')) {
        this.closeAll();
      }
    });
  }

  bindSearchOverlay() {
    // There are typically two .header__search instances (desktop + mobile).
    this.searchDetailsModals = Array.from(document.querySelectorAll('.header__search'));
    if (this.searchDetailsModals.length === 0) return;

    // Activate BOTH overlays (desktop mega-menu-overlay + mobile-menu-overlay)
    // so the dim+blur shows on every viewport — CSS media queries make sure
    // only the appropriate one is actually painted.
    const mobileOverlay = document.querySelector('.mobile-menu-overlay');

    const updateOverlay = () => {
      const anyOpen = this.searchDetailsModals.some(dm => {
        const d = dm.querySelector('details');
        return d && d.hasAttribute('open');
      });
      if (anyOpen) {
        if (this.overlay) this.overlay.classList.add('is-active');
        if (mobileOverlay) mobileOverlay.classList.add('is-active');
      } else {
        // Only drop the desktop overlay if no mega-menu item is keeping it alive.
        const megaMenuActive = Array.from(this.menuItems).some(i => i.classList.contains('is-active'));
        if (this.overlay && !megaMenuActive) this.overlay.classList.remove('is-active');
        // Only drop the mobile overlay if the mobile menu drawer isn't open.
        const shopMenu = document.querySelector('.shop-mobile-menu');
        const mobileMenuActive = shopMenu && shopMenu.classList.contains('open');
        if (mobileOverlay && !mobileMenuActive) mobileOverlay.classList.remove('is-active');
      }
    };

    this.searchDetailsModals.forEach(detailsModal => {
      const details = detailsModal.querySelector('details');
      if (!details) return;
      // Toggle the overlay whenever this search modal opens or closes.
      new MutationObserver(updateOverlay).observe(details, {
        attributes: true,
        attributeFilter: ['open'],
      });

      // Close search when clicking on the empty area of the search modal
      // (the area outside of `.search-modal__content`).
      const searchModalEl = details.querySelector('.search-modal');
      if (searchModalEl) {
        searchModalEl.addEventListener('click', (e) => {
          if (!e.target.closest('.search-modal__content')) {
            this.closeSearch();
          }
        });
      }
    });

    // Clicking the mobile overlay should close the search too.
    if (mobileOverlay) {
      mobileOverlay.addEventListener('click', () => this.closeSearch());
    }
  }

  closeSearch() {
    const modals = this.searchDetailsModals && this.searchDetailsModals.length
      ? this.searchDetailsModals
      : Array.from(document.querySelectorAll('.header__search'));
    modals.forEach(dm => {
      if (dm && typeof dm.close === 'function') dm.close(false);
    });
  }

  bindMobileEvents() {
    document.querySelectorAll('[data-mobile-menu-trigger]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = btn.getAttribute('data-mobile-menu-trigger');
        const panel = document.querySelector(`[data-mobile-subpanel="${targetId}"]`);
        if (panel) {
          const menu = panel.closest('.shop-mobile-menu');
          if (menu) menu.classList.add('has-subpanel');
          panel.classList.add('is-open');
          this.syncMobileMenuHeight();
        }
      });
    });

    document.querySelectorAll('[data-mobile-subpanel-back]').forEach(btn => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('.mobile-menu-subpanel');
        if (panel) {
          const menu = panel.closest('.shop-mobile-menu');
          panel.classList.remove('is-open');
          if (menu) menu.classList.remove('has-subpanel');
          this.syncMobileMenuHeight();
        }
      });
    });

    document.querySelectorAll('[data-accordion-trigger]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const accordion = trigger.closest('.mobile-accordion');
        if (accordion) {
          accordion.classList.toggle('is-open');
          // Wait for accordion's grid-rows transition to finish, then resync.
          setTimeout(() => this.syncMobileMenuHeight(), 360);
        }
      });
    });

    // Observe burger toggle (class .open on .shop-mobile-menu) so height
    // is computed once the panel opens/closes.
    const shopMenu = document.querySelector('.shop-mobile-menu');
    if (shopMenu) {
      const observer = new MutationObserver(() => this.syncMobileMenuHeight());
      observer.observe(shopMenu, { attributes: true, attributeFilter: ['class'] });
    }

    window.addEventListener('resize', () => this.syncMobileMenuHeight());

    // The bottom menu image starts collapsed (menu height:0) so it may finish
    // loading after the first open. Re-sync once it loads so the slide height
    // accounts for it and the image is laid out correctly.
    document.querySelectorAll('[data-mobile-menu-image]').forEach(img => {
      if (img.complete) return;
      img.addEventListener('load', () => this.syncMobileMenuHeight());
    });
  }

  /**
   * Set the slide-viewport height to match whichever slide is currently
   * active (main or sub-panel). Capped at viewport so very long menus scroll.
   */
  syncMobileMenuHeight() {
    const menu = document.querySelector('.shop-mobile-menu');
    if (!menu || !menu.classList.contains('open')) return;

    const wrap = menu.querySelector('.mobile-mega-menu');
    if (!wrap) return;

    const activeSubpanel = menu.querySelector('.mobile-menu-subpanel.is-open');
    const main = menu.querySelector('.mobile-mega-menu__main');
    const activeSlide = activeSubpanel || main;
    if (!activeSlide) return;

    // Account for vertical space taken by top/info bars (≈102px) plus a little
    // breathing room. The menu itself caps at this max-height in CSS.
    const maxH = window.innerHeight - 102;
    const desired = Math.min(activeSlide.scrollHeight, maxH);
    wrap.style.height = desired + 'px';
  }

  bindKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAll();
        this.closeSearch();
        const shopMenu = document.querySelector('.shop-mobile-menu');
        const shopAction = document.querySelector('#menu_checkbox');
        if (shopMenu && shopMenu.classList.contains('open') && shopAction) {
          shopAction.click();
        }
      }
    });

    this.menuItems.forEach(item => {
      const dropdown = item.querySelector('.mega-menu-dropdown');
      if (!dropdown) return;

      const links = dropdown.querySelectorAll('a, button');
      if (links.length > 0) {
        const lastLink = links[links.length - 1];
        lastLink.addEventListener('blur', (e) => {
          if (!item.contains(e.relatedTarget)) {
            this.close(item);
          }
        });
      }
    });
  }

  open(item) {
    const timer = this.closeTimers.get(item);
    if (timer) {
      clearTimeout(timer);
      this.closeTimers.delete(item);
    }

    this.menuItems.forEach(other => {
      if (other !== item && other.classList.contains('is-active')) {
        this.close(other);
      }
    });

    item.classList.add('is-active');

    if (this.overlay) {
      this.overlay.classList.add('is-active');
    }

    this.updateTriggerStates(item);
  }

  scheduleClose(item) {
    const timer = setTimeout(() => {
      this.close(item);
      this.closeTimers.delete(item);
    }, this.CLOSE_DELAY);
    this.closeTimers.set(item, timer);
  }

  isSearchOpen() {
    return this.searchDetailsModals && this.searchDetailsModals.some(dm => {
      const d = dm.querySelector('details');
      return d && d.hasAttribute('open');
    });
  }

  close(item) {
    item.classList.remove('is-active');

    const anyOpen = Array.from(this.menuItems).some(i => i.classList.contains('is-active'));
    if (!anyOpen && this.overlay && !this.isSearchOpen()) {
      this.overlay.classList.remove('is-active');
    }

    this.resetTriggerStates();
  }

  closeAll() {
    this.menuItems.forEach(item => {
      item.classList.remove('is-active');
      const timer = this.closeTimers.get(item);
      if (timer) clearTimeout(timer);
    });
    this.closeTimers.clear();

    if (this.overlay && !this.isSearchOpen()) {
      this.overlay.classList.remove('is-active');
    }

    this.resetTriggerStates();
  }

  updateTriggerStates(activeItem) {
    this.menuItems.forEach(item => {
      const trigger = item.querySelector('.mega-menu-trigger');
      if (!trigger) return;

      if (item === activeItem) {
        trigger.style.color = '#000';
      } else {
        trigger.style.color = '#b3b3b3';
      }
    });
  }

  resetTriggerStates() {
    const anyOpen = Array.from(this.menuItems).some(i => i.classList.contains('is-active'));
    if (anyOpen) return;

    this.menuItems.forEach(item => {
      const trigger = item.querySelector('.mega-menu-trigger');
      if (!trigger) return;
      trigger.style.color = '';
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new MegaMenuController();
});
